import { notifyError } from "common/utils/app-monitoring/notify";
import { track } from "jiffy-analytics";

const $getCsrfMetaTag = () => document.querySelector('meta[name="csrf-token"]');
const $getCsrfInputs = () => document.querySelectorAll('input[name="authenticity_token"]');
const getCurrentCsrfToken = () => $getCsrfMetaTag()?.getAttribute("content") || "";

const setCsrfMetaTag = token => {
  $getCsrfMetaTag()?.setAttribute("content", token);
};

const setCsrfInputs = token => {
  $getCsrfInputs().forEach($input => $input.setAttribute("value", token));
};

const notifyCsrfMonitoring = (message, error, source) => {
  const params = {
    error: error?.message,
    status: error?.status,
    url: window.location.href,
    source
  };
  const eventName = message.toLowerCase().replaceAll(" ", "_");

  notifyError(message, params);
  track(eventName, params);
};

const updateCsrfTokens = (token, source) => {
  try {
    setCsrfMetaTag(token);
    setCsrfInputs(token);
    return true;
  } catch (error) {
    notifyCsrfMonitoring("CSRF token update failed", error, source);
    return false;
  }
};

const refreshCsrfToken = async source => {
  try {
    const response = await fetch("/api/refresh_token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCurrentCsrfToken()
      }
    });

    if (!response.ok) {
      throw new Error("Failed to refresh CSRF token");
    }

    const data = await response.json();
    return updateCsrfTokens(data.csrf_token, source);
  } catch (error) {
    notifyCsrfMonitoring("CSRF token refresh failed", error, source);
    return false;
  }
};

const isSessionExpired = error => {
  try {
    const parsed = JSON.parse(error.message);
    if (parsed?.error === "Session expired") return true;
  } catch {
    // not JSON — check for Rails CSRF exception in HTML response
  }

  return (
    error.status === 422 &&
    typeof error.message === "string" &&
    (error.message.includes("InvalidAuthenticityToken") ||
      error.message.includes("CSRF token authenticity"))
  );
};

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

export { refreshCsrfToken, getCurrentCsrfToken, withCsrfRetry, isSessionExpired };
